import axios, { AxiosError } from 'axios'

import environment from '../environment'

const defaultOptions = {
  baseURL: environment.apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  maxContentLength: 50 * 1024 * 1024, // 50 MB
  maxBodyLength: 50 * 1024 * 1024, // 50 MB
}

const instance = axios.create(defaultOptions)

let isRefreshing = false
let failedRequestsQueue: { resolve: (token: string) => void; reject: (error: unknown) => void }[] = []

//doing the requests that failed due to token expiration with the new token
const processQueue = (error: unknown, token: string | null = null) => {
  failedRequestsQueue.forEach(prom => {
    if (token) {
      prom.resolve(token)
    } else {
      prom.reject(error)
    }
  })
  failedRequestsQueue = []
}

//adding token to requests
instance.interceptors.request.use(config => {
  if (config.baseURL?.includes('auth') || config.baseURL?.includes('approveTransaction')) {
    return config
  }
  const tokens = JSON.parse(localStorage.getItem('cognito-tokens') || '{}')
  if (tokens?.accessToken) {
    config.headers!.Authorization = `Bearer ${tokens.accessToken}`
  }
  return config
})

//handling token expiration if there was error response
instance.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config
    if (!originalRequest._retry && error.response && (error.response.status === 401 || error.response.status === 403)) {
      const tokens = JSON.parse(localStorage.getItem('cognito-tokens') || '{}')
      if (!tokens?.refreshToken) {
        localStorage.removeItem('cognito-tokens')
        window.location.reload()
        return Promise.reject(error)
      }
      //if we had error while we refresh we add it to queue so we wont have concurrent requests
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedRequestsQueue.push({
            resolve: token => {
              originalRequest.headers.Authorization = `Bearer ${token}`
              resolve(axios(originalRequest))
            },
            reject: err => {
              reject(err)
            },
          })
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const { data } = await axios.post(`${environment.apiBaseUrl}/auth/refresh-token`, {
          refreshToken: tokens.refreshToken,
        })

        localStorage.setItem('cognito-tokens', JSON.stringify({ ...tokens, accessToken: data.accessToken }))

        processQueue(null, data.accessToken)
        isRefreshing = false

        originalRequest.headers.Authorization = `Bearer ${data.accessToken}`
        return axios(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        localStorage.removeItem('cognito-tokens')
        window.location.reload()
        return Promise.reject(refreshError)
      }
    }
    console.error('API ERROR', error)
    return Promise.reject(error)
  }
)
export const getErrorMessage = (error: unknown) => {
  if (!error) return 'Unknown error'
  return (
    (error as ErrorResponse).response?.data?.msg ||
    (error as ErrorResponse).response?.data?.error ||
    (error as Error).message ||
    JSON.stringify(error)
  )
}
export type ApiResponse<T> = {
  success: boolean
  message: string
  data: T
  response?: T
  msg?: string
  error?: string
}
export default instance
export type ErrorResponse = AxiosError<{
  msg?: string
  error?: string
}>
